/**
 * Settings → Users — SUPER_ADMIN only.
 *
 * The single supported surface for changing roles, deactivating users,
 * resending invitations, and triggering admin-driven password resets.
 *
 * Safety mirroring backend invariants:
 *   * The "Change role" + "Deactivate" actions are hidden on the
 *     current Super Admin's own row — they wouldn't work anyway
 *     (server rejects with 422), but skipping the affordance avoids
 *     foot-guns.
 *   * Role changes go through a confirmation modal — accidental
 *     promotion to SUPER_ADMIN is the worst-case foot-gun, so we
 *     always make the admin click twice.
 *   * Last-Super-Admin guard surfaces as a server 422 with a clear
 *     message; we just relay it through the toast so the SPA stays
 *     simple.
 */
import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  KeyRound,
  Loader2,
  MoreHorizontal,
  Search,
  Send,
  ShieldAlert,
  ShieldCheck,
  UserCheck,
  UserX,
} from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";
import { formatDate } from "@/lib/utils";
import {
  adminResetPassword,
  changeUserRole,
  deactivateUser,
  listSettingsUsers,
  reactivateUser,
  resendInvite,
  type SettingsUserRole,
  type SettingsUserRow,
  type SettingsUserStatus,
} from "@/api/settingsUsers";

const ROLE_OPTIONS: { value: SettingsUserRole; label: string }[] = [
  { value: "EMPLOYEE", label: "Employee" },
  { value: "MANAGER", label: "Manager" },
  { value: "HR_ADMIN", label: "HR Admin" },
  { value: "SUPER_ADMIN", label: "Super Admin" },
];

const ROLE_LABELS: Record<SettingsUserRole, string> = {
  EMPLOYEE: "Employee",
  MANAGER: "Manager",
  HR_ADMIN: "HR Admin",
  SUPER_ADMIN: "Super Admin",
};

// Color tokens for the four role badges. Gold + purple aren't in the
// shared Badge variants so we render bespoke pills here.
const ROLE_BADGE_CLASS: Record<SettingsUserRole, string> = {
  EMPLOYEE: "bg-text-muted/10 text-text-muted",
  MANAGER: "bg-accent/10 text-accent",
  HR_ADMIN: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
  SUPER_ADMIN: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
};

const STATUS_LABELS: Record<SettingsUserStatus, string> = {
  active: "Active",
  pending_setup: "Pending Setup",
  inactive: "Inactive",
};

const STATUS_BADGE_CLASS: Record<SettingsUserStatus, string> = {
  active: "bg-success/10 text-success",
  pending_setup: "bg-warning/10 text-warning",
  inactive: "bg-danger/10 text-danger",
};

type ConfirmAction =
  | { kind: "role"; user: SettingsUserRow; nextRole: SettingsUserRole }
  | { kind: "reset"; user: SettingsUserRow }
  | { kind: "deactivate"; user: SettingsUserRow }
  | { kind: "reactivate"; user: SettingsUserRow }
  | { kind: "resend"; user: SettingsUserRow };

export function UsersSettings() {
  const { addToast } = useToast();
  const { user: me } = useAuth();
  const [rows, setRows] = useState<SettingsUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<SettingsUserRole | "all">("all");
  const [statusFilter, setStatusFilter] = useState<
    SettingsUserStatus | "all"
  >("all");
  const [sort, setSort] = useState<"name" | "last_login" | "created">("name");
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [pending, setPending] = useState<ConfirmAction | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSettingsUsers({
        search: search.trim() || undefined,
        role: roleFilter === "all" ? undefined : roleFilter,
        status: statusFilter === "all" ? undefined : statusFilter,
        sort,
      });
      setRows(data);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      setError(ax.response?.data?.detail || "Couldn't load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, statusFilter, sort]);

  // Debounce the search input — fire 300ms after typing stops so we
  // don't spam the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const closeMenu = () => setOpenMenu(null);

  const handleConfirm = async () => {
    if (!pending) return;
    setActionBusy(true);
    try {
      if (pending.kind === "role") {
        await changeUserRole(pending.user.id, pending.nextRole);
        addToast(
          "success",
          `${pending.user.name} is now ${ROLE_LABELS[pending.nextRole]}`,
        );
      } else if (pending.kind === "reset") {
        await adminResetPassword(pending.user.id);
        addToast("success", `Reset email sent to ${pending.user.email}`);
      } else if (pending.kind === "deactivate") {
        await deactivateUser(pending.user.id);
        addToast("success", `${pending.user.name} has been deactivated`);
      } else if (pending.kind === "reactivate") {
        await reactivateUser(pending.user.id);
        addToast("success", `${pending.user.name} has been reactivated`);
      } else if (pending.kind === "resend") {
        await resendInvite(pending.user.id);
        addToast("success", `Invitation resent to ${pending.user.email}`);
      }
      setPending(null);
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        ax.response?.data?.detail || "That action failed. Try again.",
      );
    } finally {
      setActionBusy(false);
    }
  };

  const isMe = (row: SettingsUserRow) => row.id === me?.id;

  const counts = useMemo(() => {
    const total = rows.length;
    const supers = rows.filter(
      (r) => r.role === "SUPER_ADMIN" && r.status === "active",
    ).length;
    return { total, supers };
  }, [rows]);

  return (
    <div className="space-y-4" onClick={closeMenu}>
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <span className="text-xs text-text-muted">
            {counts.total} user{counts.total === 1 ? "" : "s"} ·{" "}
            {counts.supers} active Super Admin
            {counts.supers === 1 ? "" : "s"}
          </span>
        </CardHeader>

        <p className="text-xs text-text-muted -mt-2 mb-3">
          New accounts always start as <strong>Employee</strong>. Changing
          someone&rsquo;s role here is the only way to grant admin access.
        </p>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email"
            icon={<Search className="h-4 w-4" />}
          />
          <Select
            value={roleFilter}
            onChange={(e) =>
              setRoleFilter(e.target.value as SettingsUserRole | "all")
            }
            options={[
              { value: "all", label: "All roles" },
              ...ROLE_OPTIONS,
            ]}
          />
          <Select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as SettingsUserStatus | "all")
            }
            options={[
              { value: "all", label: "All statuses" },
              { value: "active", label: "Active" },
              { value: "pending_setup", label: "Pending Setup" },
              { value: "inactive", label: "Inactive" },
            ]}
          />
          <Select
            value={sort}
            onChange={(e) =>
              setSort(e.target.value as "name" | "last_login" | "created")
            }
            options={[
              { value: "name", label: "Sort: Name" },
              { value: "last_login", label: "Sort: Last login" },
              { value: "created", label: "Sort: Created" },
            ]}
          />
        </div>

        {error && (
          <div className="rounded-md bg-danger/10 border border-danger/20 px-3 py-2 mb-3 flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center text-sm text-text-muted">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-text-muted">
            No users match this filter.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-text-muted border-b border-border/50">
                <tr>
                  <th className="text-left py-2 px-2 font-medium">Name</th>
                  <th className="text-left py-2 px-2 font-medium">Email</th>
                  <th className="text-left py-2 px-2 font-medium">Role</th>
                  <th className="text-left py-2 px-2 font-medium">Status</th>
                  <th className="text-left py-2 px-2 font-medium">
                    Last login
                  </th>
                  <th className="text-right py-2 px-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-surface-hover/40">
                    <td className="py-2 px-2 text-text-primary">
                      {r.name}
                      {isMe(r) && (
                        <span className="ml-2 text-xs text-text-muted">
                          (you)
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-text-muted">{r.email}</td>
                    <td className="py-2 px-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_BADGE_CLASS[r.role]}`}
                      >
                        {ROLE_LABELS[r.role]}
                      </span>
                    </td>
                    <td className="py-2 px-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE_CLASS[r.status]}`}
                      >
                        {STATUS_LABELS[r.status]}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-text-muted">
                      {r.last_login_at
                        ? formatDate(r.last_login_at)
                        : "Never"}
                    </td>
                    <td className="py-2 px-2 text-right relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenu(openMenu === r.id ? null : r.id);
                        }}
                        className="p-1 rounded hover:bg-surface-hover"
                        title="Actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                      {openMenu === r.id && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="absolute right-2 top-9 z-10 w-56 rounded-md border border-border bg-background shadow-lg py-1 text-left"
                        >
                          {/* Change role — hidden on own row to
                              prevent self-lockout. */}
                          {!isMe(r) && (
                            <RoleSubmenu
                              currentRole={r.role}
                              onPick={(nextRole) => {
                                setOpenMenu(null);
                                setPending({ kind: "role", user: r, nextRole });
                              }}
                            />
                          )}
                          <button
                            onClick={() => {
                              setOpenMenu(null);
                              setPending({ kind: "reset", user: r });
                            }}
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-surface-hover flex items-center gap-2"
                          >
                            <KeyRound className="h-4 w-4" />
                            Reset password
                          </button>
                          {r.status === "pending_setup" && (
                            <button
                              onClick={() => {
                                setOpenMenu(null);
                                setPending({ kind: "resend", user: r });
                              }}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-surface-hover flex items-center gap-2"
                            >
                              <Send className="h-4 w-4" />
                              Resend invitation
                            </button>
                          )}
                          {r.status === "inactive" && (
                            <button
                              onClick={() => {
                                setOpenMenu(null);
                                setPending({ kind: "reactivate", user: r });
                              }}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-surface-hover flex items-center gap-2"
                            >
                              <UserCheck className="h-4 w-4 text-success" />
                              Reactivate
                            </button>
                          )}
                          {r.status !== "inactive" && !isMe(r) && (
                            <button
                              onClick={() => {
                                setOpenMenu(null);
                                setPending({ kind: "deactivate", user: r });
                              }}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-surface-hover flex items-center gap-2 text-danger"
                            >
                              <UserX className="h-4 w-4" />
                              Deactivate
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Confirmation modal — one component handles all four action
          types. Copy varies, plumbing doesn't. */}
      <Modal
        isOpen={pending !== null}
        onClose={() => (actionBusy ? null : setPending(null))}
        title={confirmTitle(pending)}
      >
        {pending && (
          <>
            <ConfirmBody pending={pending} />
            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setPending(null)}
                disabled={actionBusy}
              >
                Cancel
              </Button>
              <Button onClick={handleConfirm} isLoading={actionBusy}>
                {confirmCta(pending)}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

function RoleSubmenu({
  currentRole,
  onPick,
}: {
  currentRole: SettingsUserRole;
  onPick: (r: SettingsUserRole) => void;
}) {
  return (
    <div className="px-3 py-1.5 border-b border-border/40">
      <p className="text-[11px] uppercase tracking-wide text-text-muted mb-1">
        Change role
      </p>
      {ROLE_OPTIONS.map((opt) => {
        const isCurrent = opt.value === currentRole;
        return (
          <button
            key={opt.value}
            disabled={isCurrent}
            onClick={() => onPick(opt.value)}
            className={`w-full text-left px-2 py-1 text-sm rounded flex items-center justify-between ${
              isCurrent
                ? "text-text-muted cursor-default"
                : "hover:bg-surface-hover text-text-primary"
            }`}
          >
            <span className="flex items-center gap-2">
              {opt.value === "SUPER_ADMIN" ? (
                <ShieldAlert className="h-3.5 w-3.5 text-amber-500" />
              ) : opt.value === "HR_ADMIN" ? (
                <ShieldCheck className="h-3.5 w-3.5 text-purple-500" />
              ) : null}
              {opt.label}
            </span>
            {isCurrent && (
              <CheckCircle2 className="h-3.5 w-3.5 text-success" />
            )}
          </button>
        );
      })}
    </div>
  );
}

function confirmTitle(pending: ConfirmAction | null): string {
  if (!pending) return "";
  switch (pending.kind) {
    case "role":
      return "Change role?";
    case "reset":
      return "Send password reset?";
    case "deactivate":
      return "Deactivate user?";
    case "reactivate":
      return "Reactivate user?";
    case "resend":
      return "Resend invitation?";
  }
}

function confirmCta(pending: ConfirmAction): string {
  switch (pending.kind) {
    case "role":
      return `Make ${ROLE_LABELS[pending.nextRole]}`;
    case "reset":
      return "Send reset email";
    case "deactivate":
      return "Deactivate";
    case "reactivate":
      return "Reactivate";
    case "resend":
      return "Resend invitation";
  }
}

function ConfirmBody({ pending }: { pending: ConfirmAction }) {
  if (pending.kind === "role") {
    const isPromoteToSuper = pending.nextRole === "SUPER_ADMIN";
    return (
      <div className="text-sm text-text-muted space-y-2">
        <p>
          Change <strong className="text-text-primary">{pending.user.name}</strong>
          &rsquo;s role from{" "}
          <strong>{ROLE_LABELS[pending.user.role]}</strong> to{" "}
          <strong className="text-text-primary">
            {ROLE_LABELS[pending.nextRole]}
          </strong>
          ?
        </p>
        {isPromoteToSuper && (
          <p className="rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-amber-700 dark:text-amber-400">
            Super Admins can manage every user, settings page, billing
            details, and audit log. Only grant this to people you trust
            completely.
          </p>
        )}
        <p className="text-xs">
          Effective immediately. If this is a demotion, the user will be
          signed out on their next API call.
        </p>
      </div>
    );
  }
  if (pending.kind === "reset") {
    return (
      <p className="text-sm text-text-muted">
        We&rsquo;ll email a password reset link to{" "}
        <strong className="text-text-primary">{pending.user.email}</strong>.
        The link expires in 24 hours.
      </p>
    );
  }
  if (pending.kind === "deactivate") {
    return (
      <p className="text-sm text-text-muted">
        This will immediately sign{" "}
        <strong className="text-text-primary">{pending.user.name}</strong>{" "}
        out and prevent future logins. Their employee record is preserved
        and can be reactivated later.
      </p>
    );
  }
  if (pending.kind === "reactivate") {
    return (
      <p className="text-sm text-text-muted">
        Reactivate <strong>{pending.user.name}</strong>? They&rsquo;ll be
        able to sign in again and we&rsquo;ll email them to let them know.
      </p>
    );
  }
  return (
    <p className="text-sm text-text-muted">
      Send a fresh setup-password link to{" "}
      <strong className="text-text-primary">{pending.user.email}</strong>.
      Any earlier invitation links will stop working.
    </p>
  );
}

export default UsersSettings;
